import { useEffect, useState } from 'react';
import { Skeleton, useAsync } from '../../components/ui';
import { pct, pp, Report, VersionRow } from '../../journeys/model';
import { api } from '../../lib/api';
import { day, num } from '../../lib/format';
import { currentLocale, t } from '../../lib/i18n';
import { errorText } from '../Contacts';

function RateRow({ label, rate, entered, goal, max }: { label: string; rate: number; entered: number; goal: number; max: number }) {
  return (
    <div className="rate-row">
      <span>{label}</span>
      <span className="bar"><i style={{ width: `${max ? Math.min(100, (rate / max) * 100) : 0}%` }} /></span>
      <span className="small"><b>{pct(rate)}</b> · {t('{g} из {e}', { g: num(goal), e: num(entered) })}</span>
    </div>
  );
}

/** The interval of the lift on a scale symmetric around zero. */
function LiftBar({ lift, ci, significant }: { lift: number; ci: [number, number]; significant: boolean | null }) {
  const span = Math.max(0.02, Math.abs(ci[0]), Math.abs(ci[1]), Math.abs(lift)) * 1.15;
  const x = (v: number) => `${50 + (v / span) * 50}%`;
  return (
    <div className="lift-bar" role="img" aria-label={t('Прирост {lift}, 95 % интервал от {a} до {b}', { lift: pp(lift), a: pp(ci[0]), b: pp(ci[1]) })}>
      <span className="zero" style={{ left: '50%' }} />
      <span className={`ci${significant ? ' sig' : ''}`} style={{ left: x(ci[0]), width: `calc(${x(ci[1])} - ${x(ci[0])})` }} />
      <span className="pt" style={{ left: `calc(${x(lift)} - 2px)` }} />
    </div>
  );
}

/**
 * Report (docs/journeys/SPEC.md §3.12, §4.1): two separate blocks. «Прирост» only against the holdout control group,
 * with the interval and the sample size; «Атрибутировано» is labelled as not equal to the lift and never stands in.
 */
export function JourneyReport({ journeyId, versions, defaultVersion, onEnableHoldout }: { journeyId: string; versions: VersionRow[]; defaultVersion: number | null; onEnableHoldout?: () => void }) {
  const [version, setVersion] = useState<number | null>(defaultVersion);
  useEffect(() => setVersion(defaultVersion), [defaultVersion]);
  const r = useAsync(() => (version ? api.get<Report>(`/journeys/${journeyId}/report?version=${version}`) : Promise.resolve(null)), [journeyId, version]);
  const d = r.data;
  return (
    <div className="stack">
      <div className="row wrap">
        <label className="small">{t('Версия')}
          <select className="select input" style={{ marginLeft: 6, width: 'auto' }} value={version ?? ''} onChange={(e) => setVersion(Number(e.target.value) || null)}>
            {versions.map((v) => <option key={v.version} value={v.version}>v{v.version}{v.published_at ? ` · ${t('опубликована')}` : ''}</option>)}
          </select>
        </label>
        {d && <span className="small muted">{t('Участия с {from} · в расчёте только те, у кого окно цели ({days} дн.) уже закрылось', { from: d.period.from ? day(d.period.from) : '—', days: d.window_days })}</span>}
      </div>
      {r.loading && !d ? (
        <Skeleton h={220} />
      ) : r.error ? (
        <div className="callout bad">{errorText(r.error)}</div>
      ) : !d ? (
        <div className="callout">{t('У сценария нет версий.')}</div>
      ) : (
        <>
          <div className="report-grid">
            <div className="card card-pad stack">
              <h3 style={{ margin: 0 }}>{t('Прирост (по контрольной группе)')}</h3>
              {!d.incremental.available ? (
                <>
                  <p className="small" style={{ margin: 0 }}>{d.incremental.text}</p>
                  <p className="small muted" style={{ margin: 0 }}>{t('Контрольная группа не получает писем сценария. Сравнение её доли целей с долей у получивших письма — единственный честный способ сказать, сколько добавил сценарий.')}</p>
                  {onEnableHoldout && <button className="btn btn-sm" style={{ alignSelf: 'flex-start' }} onClick={onEnableHoldout}>{t('Открыть настройки цели и holdout')}</button>}
                </>
              ) : (
                <>
                  {(() => {
                    const inc = d.incremental;
                    const max = Math.max(inc.treatment.rate, inc.control.rate, 0.0001);
                    return (
                      <>
                        <RateRow label={t('Получали письма')} rate={inc.treatment.rate} entered={inc.treatment.entered} goal={inc.treatment.goal} max={max} />
                        <RateRow label={t('Контроль')} rate={inc.control.rate} entered={inc.control.entered} goal={inc.control.goal} max={max} />
                        <div>
                          <div className="kpi" style={{ padding: 0 }}>
                            <div className="label">{t('прирост доли целей')}</div>
                            <div className="value">{inc.min_sample_ok ? pp(inc.lift_abs) : '—'}{inc.min_sample_ok && inc.lift_rel != null && <small> ({t('{s}{v} % отн.', { s: inc.lift_rel >= 0 ? '+' : '−', v: Math.abs(inc.lift_rel * 100).toFixed(0) })})</small>}</div>
                          </div>
                          <LiftBar lift={inc.lift_abs} ci={inc.ci95_abs} significant={inc.significant} />
                          <div className="small muted">{t('95 % интервал (Ньюкомб): {a} … {b}', { a: pp(inc.ci95_abs[0]), b: pp(inc.ci95_abs[1]) })}</div>
                        </div>
                        {inc.min_sample_ok ? (
                          <div className={`callout ${inc.significant ? 'ok' : ''}`}>{inc.significant ? t('Интервал не содержит нуля: разница статистически значима.') : t('Интервал содержит ноль: значимой разницы пока нет.')}</div>
                        ) : (
                          <div className="callout warn">{inc.note ?? t('Данных пока мало: выводы делать рано.')}</div>
                        )}
                        {/* A point estimate without its own interval reads as a measured effect: shown only with enough data. */}
                        {inc.min_sample_ok && (inc.value.treatment_per_contact > 0 || inc.value.control_per_contact > 0) && (
                          <div className="small">{t('Ценность на контакт: {a} против {b} в контроле (без интервала — ориентир, не вывод)', { a: Math.round(inc.value.treatment_per_contact).toLocaleString(currentLocale()), b: Math.round(inc.value.control_per_contact).toLocaleString(currentLocale()) })}</div>
                        )}
                        <div className="small muted">{t('Каждый контакт считается один раз — по первому участию в этой версии.')}</div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
            <div className="card card-pad stack">
              <h3 style={{ margin: 0 }}>{t('Атрибутировано (не равно приросту)')}</h3>
              <p className="small muted" style={{ margin: 0 }}>{d.attributed.label}. {t('Часть этих людей совершила бы действие и без письма — поэтому это число не прирост.')}</p>
              <div className="grid grid-2">
                <div className="kpi" style={{ padding: 0 }}><div className="label">{t('целей после письма')}</div><div className="value">{num(d.attributed.goal_after_email)}</div></div>
                <div className="kpi" style={{ padding: 0 }}><div className="label">{t('сумма ценности')}</div><div className="value">{d.attributed.value_sum.toLocaleString(currentLocale())}</div></div>
              </div>
            </div>
          </div>
          {Object.entries(d.variants).map(([step, rows]) => (
            <div key={step} className="card">
              <div className="card-head"><h3>{t('Сплит')} <code>{step}</code></h3><span className="small muted">{t('только получающие письма, закрытые окна')}</span></div>
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>{t('Вариант')}</th><th>{t('Участий')}</th><th>{t('Целей')}</th><th>{t('Доля')}</th><th>{t('95 % интервал (Уилсон)')}</th></tr></thead>
                  <tbody>
                    {rows.map((v) => (
                      <tr key={v.id}><td><b>{v.id}</b></td><td>{num(v.entered)}</td><td>{num(v.goal)}</td><td>{pct(v.rate)}</td><td className="small">{pct(v.ci95[0])} … {pct(v.ci95[1])}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="card-pad small muted">{t('Победитель автоматически не выбирается: пересекающиеся интервалы — не повод менять сценарий.')}</div>
            </div>
          ))}
          {d.caveats.length > 0 && (
            <ul className="small muted" style={{ margin: 0, paddingLeft: 18 }}>
              {d.caveats.map((c) => <li key={c}>{c}</li>)}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
